import { GQLResponseError } from '@change-corgi/core/gqlErrors';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import {
	PetitionSupporterVoicesFeed,
	type PetitionSupporterVoicesFeedQuery,
	type PetitionSupporterVoicesFeedQueryVariables,
} from './api.graphql';

export type SupporterVoicesFeedItem = PetitionSupporterVoicesFeedQuery['petitionSupporterVoicesFeed'][number];

export const getSupporterVoicesFeed = async (
	utilityContext: UtilityContext,
	slugOrId: string,
	{
		maxItems,
		commentsPerCycle,
		videosPerCycle,
	}: {
		maxItems: number;
		commentsPerCycle: number;
		videosPerCycle: number;
	},
): Promise<readonly SupporterVoicesFeedItem[]> => {
	const {
		gql: { fetch },
		errorReporter: { report },
	} = utilityContext;

	// supporter-voices is a non-critical section of the petition details page -- a failure here
	// must not take down the whole page, so we catch and fall back to an empty feed rather than
	// letting the error propagate and break page rendering.
	try {
		const { data } = await fetch<PetitionSupporterVoicesFeedQuery, PetitionSupporterVoicesFeedQueryVariables>({
			query: PetitionSupporterVoicesFeed,
			variables: {
				petitionSlugOrId: slugOrId,
				maxItems,
				commentsPerCycle,
				videosPerCycle,
				sortBy: 'SCORE',
			},
			rejectOnError: true,
			batched: true,
		});

		return data.petitionSupporterVoicesFeed;
	} catch (error) {
		void report(
			{
				error: new Error('Failed to load supporter voices feed', { cause: error }),
				context: {
					requestId: error instanceof GQLResponseError ? error.requestId : undefined,
				},
				params: {
					slugOrId,
					...(error instanceof GQLResponseError ? { errors: error.errors } : undefined),
				},
			},
			{ severity: 'warning' },
		);

		return [];
	}
};
